import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowRight, Eye, EyeOff, Loader2, Lock, Mail, Store } from 'lucide-react'

import { esFallaDeConexion, type ErrorApi } from '@/lib/api'
import {
  cargarTenantPublico,
  cargarUsuariosDeLaCajaWeb,
  useSesion,
  type TenantPublico,
  type UsuarioParaPin,
} from '@/lib/sesion'
import { cajaDeEsteNavegador, instalacionDeEsteNavegador } from '@/lib/instalacion'
import { config } from '@/lib/config'
import { Campo } from '@/componentes/ui/Campo'
import { PinPad } from '@/componentes/ui/PinPad'
import { PanelMarca } from './PanelMarca'

type Modo = 'password' | 'pin'

// Pantalla de entrada.
//
// Es lo primero que ve cualquiera, así que se ocupa de tres cosas antes que de
// verse bien: saber DE QUÉ LOCAL es (el slug sale de la URL), ofrecer la forma
// de entrar que corresponde al dispositivo (contraseña en el navegador, PIN en
// la caja), y decir con claridad qué pasó cuando algo falla.
export function Login() {
  const { slug: slugUrl } = useParams<{ slug?: string }>()
  const navegar = useNavigate()

  const { entrar, entrarConPin, tenant: tenantSesion, usuario } = useSesion()

  // Si la sesión aparece mientras esta pantalla está abierta, no hay nada que
  // pedirle a nadie: ya está adentro.
  //
  // Pasa de verdad en el local que trabaja todo en línea. Si el navegador se
  // abre antes de que levante el módem, la rehidratación no puede preguntar y
  // la pantalla queda acá; cuando vuelve la conexión el reintento entra solo
  // (ver `programarReintento` en lib/sesion), y sin esto el cajero seguía
  // viendo el formulario y tipeaba mail y contraseña estando adentro.
  useEffect(() => {
    if (!usuario) return

    void navegar(`/${tenantSesion?.slug ?? slugUrl ?? ''}`, { replace: true })
  }, [usuario, tenantSesion?.slug, slugUrl, navegar])

  // El local que se encontró tipeando el slug a mano. El que viene de la URL
  // lo resuelve la consulta de más abajo.
  const [tenantManual, setTenantManual] = useState<TenantPublico | null>(tenantSesion)

  // En la caja el modo por defecto es PIN: nadie tipea una contraseña de
  // dieciséis caracteres con las manos mojadas y seis personas esperando.
  const [modo, setModo] = useState<Modo>(
    config().runtime === 'caja' || cajaDeEsteNavegador() !== null ? 'pin' : 'password',
  )

  const [slug, setSlug] = useState(slugUrl ?? '')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')
  const [verPassword, setVerPassword] = useState(false)

  // ── Entrada con PIN desde el navegador ──
  //
  // Es para el local que trabaja todo en línea: la tablet del mostrador cambia
  // de cajero con cuatro dígitos, como la caja del mostrador, en lugar de
  // tipear mail y contraseña en cada relevo. Y eso importa más de lo que
  // parece: si todos entran con el mismo usuario porque es lo cómodo, el
  // arqueo deja de decir quién estuvo en el turno.
  //
  // Solo se ofrece si este navegador está REGISTRADO como caja (se hace una
  // vez desde Configuración → Cajas): un PIN de cuatro dígitos no puede ser
  // una credencial válida desde cualquier lado de internet.
  const cajaDelNavegador = cajaDeEsteNavegador()
  const esCajaInstalada = config().runtime === 'caja'
  const puedePin = esCajaInstalada || cajaDelNavegador !== null

  const [elegido, setElegido] = useState<UsuarioParaPin | null>(null)

  const [enviando, setEnviando] = useState(false)
  const [errorFormulario, setError] = useState<string | null>(null)
  const [errores, setErrores] = useState<Record<string, string[]>>({})

  const refEmail = useRef<HTMLInputElement>(null)
  const refSlug = useRef<HTMLInputElement>(null)

  // Se resuelve el local del slug de la URL para poder mostrar su nombre y su
  // logo antes de que nadie se autentique.
  //
  // Va con useQuery y no con un efecto: el efecto tenía que prender el estado
  // de carga, cancelarse con una bandera y atrapar el error a mano, y además
  // llamaba a setState en su propio cuerpo, que dispara un render de más en
  // cada montaje. La consulta hace las tres cosas sola, y de paso cachea: al
  // volver al login desde la sesión caída, el nombre del local ya está.
  const consultaLocal = useQuery({
    queryKey: ['tenant-publico', slugUrl],
    queryFn: () => cargarTenantPublico(slugUrl!),
    enabled: !!slugUrl && !tenantSesion,

    // Los datos públicos del local (nombre, logo) no cambian en una sesión.
    staleTime: Infinity,

    // Un local que el servidor dice que no existe no se vuelve a pedir. Una
    // falla de conexión sí, y con paciencia: esta es la pantalla que el cajero
    // tiene adelante mientras espera que vuelva internet, y tiene que
    // arrancar sola cuando vuelve en lugar de quedarse pidiendo un F5.
    retry: (intentos, e) => esFallaDeConexion(e) && intentos < 60,
    retryDelay: 5000,
  })

  // «Buscando el local…» solo mientras de verdad se está buscando: con el
  // reintento de más arriba, la consulta se queda en `isFetching` para
  // siempre si no hay conexión, y el cajero miraba un cartel de carga eterno
  // sin que nada le dijera que el problema es la red.
  const buscandoLocal = consultaLocal.isFetching && consultaLocal.failureCount === 0

  // El resultado de la consulta manda sobre el estado local, que solo existe
  // para la búsqueda manual del slug.
  const tenant = consultaLocal.data ?? tenantManual

  // Un fallo de la consulta es SIEMPRE de conexión: un local que no existe
  // vuelve como `null` y cuenta como éxito (ver `cargarTenantPublico`). Se
  // mira `failureCount` y no `isError` para poder decirlo desde el primer
  // intento fallido, sin esperar a que se agoten los reintentos.
  const errorLocal =
    consultaLocal.failureCount > 0
      ? 'No pudimos comunicarnos con el sistema. Revisá la conexión.'
      : consultaLocal.isSuccess && !consultaLocal.data
        ? `No encontramos el local «${slugUrl ?? ''}».`
        : null

  // Un solo mensaje visible: el del formulario tapa al del local, porque es la
  // respuesta a lo último que hizo el usuario.
  const error = errorFormulario ?? errorLocal

  // El foco arranca donde el usuario va a tipear. Sin esto hay un click de
  // más en cada entrada, y son varias por día.
  useEffect(() => {
    if (buscandoLocal) return
    if (!tenant) refSlug.current?.focus()
    else if (modo === 'password') refEmail.current?.focus()
  }, [buscandoLocal, tenant, modo])

  const buscarLocal = async (e: React.FormEvent) => {
    e.preventDefault()
    const limpio = slug.trim().toLowerCase()
    if (!limpio) return

    setEnviando(true)
    setError(null)

    let t: TenantPublico | null

    try {
      t = await cargarTenantPublico(limpio)
    } catch {
      // No llegamos a preguntar: el nombre del local puede estar perfecto.
      setEnviando(false)
      setError('No pudimos comunicarnos con el sistema. Revisá la conexión.')
      return
    }

    setEnviando(false)

    if (!t) {
      setError('No encontramos ese local. Revisá el nombre con quien te dio el acceso.')
      return
    }

    setTenantManual(t)
    // Queda en la URL para que se pueda guardar en favoritos.
    void navegar(`/${limpio}/login`, { replace: true })
  }

  // Los empleados que pueden entrar con PIN en este dispositivo. Se piden
  // recién cuando hace falta (modo PIN y local resuelto) porque antes de eso no
  // hay ni con qué pedirlos.
  const consultaEmpleados = useQuery({
    queryKey: ['pin', 'empleados', tenant?.slug],
    queryFn: () => cargarUsuariosDeLaCajaWeb(tenant!.slug, instalacionDeEsteNavegador()),
    enabled: modo === 'pin' && !esCajaInstalada && !!tenant && cajaDelNavegador !== null,
    retry: false,
    staleTime: 5 * 60_000,
  })

  const empleados = consultaEmpleados.data ?? []

  // Con un solo empleado habilitado no hay nada que elegir: se salta el paso.
  // Es el caso de la verdulería chica, que es la mayoría.
  //
  // Se DERIVA en lugar de guardarse con un efecto: un setState dentro de un
  // efecto dispara un render de más y, con la lista llegando de una consulta,
  // se ejecuta cada vez que cambia la referencia del array.
  const cajero = elegido ?? (empleados.length === 1 ? empleados[0]! : null)

  const enviar = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!tenant || enviando) return

    setEnviando(true)
    setError(null)
    setErrores({})

    try {
      if (modo === 'password') {
        await entrar(tenant.slug, email.trim(), password)
      } else {
        if (!cajero) {
          setError('Tocá tu nombre antes de poner el PIN.')
          return
        }

        await entrarConPin(tenant.slug, cajero.id, pin, obtenerInstalacionId())
      }

      void navegar(`/${tenant.slug}`, { replace: true })
    } catch (err) {
      const e = err as ErrorApi
      setError(e.message)
      if (e.errores) setErrores(e.errores)

      // El PIN se limpia siempre que falla: dejarlo cargado invita a apretar
      // Enter de nuevo con el mismo valor y gastar un intento del bloqueo.
      if (modo === 'pin') setPin('')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <PanelMarca tenant={tenant} cargando={buscandoLocal} />

      {/* Formulario. En el celular arranca arriba y no centrado: con la banda
          de marca ocupando la cabecera, centrar en el resto deja el campo de
          email a media pantalla y el teclado virtual lo tapa. */}
      <main className="flex flex-1 items-start justify-center px-5 pb-10 pt-8 lg:items-center lg:px-12 lg:py-10">
        <div className="w-full max-w-[400px] animate-entrar">
          {!tenant && !buscandoLocal ? (
            <PasoLocal
              slug={slug}
              setSlug={setSlug}
              onEnviar={buscarLocal}
              enviando={enviando}
              error={error}
              refSlug={refSlug}
            />
          ) : (
            <>
              <header className="mb-7">
                {/* En el celular el nombre del local ya está en la banda de
                    arriba: repetirlo acá lo dice dos veces en 200 píxeles. */}
                <p className="mb-1.5 hidden text-[13px] font-semibold uppercase tracking-wide text-verde lg:block">
                  {tenant?.nombre ?? 'Cargando…'}
                </p>
                <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-tinta">
                  {modo === 'pin' ? 'Ingresá tu PIN' : 'Entrá a tu cuenta'}
                </h1>
                <p className="mt-1.5 text-[14px] text-tinta-tenue">
                  {modo === 'pin'
                    ? 'El mismo que usás para abrir la caja.'
                    : 'Con el mail y la contraseña de tu usuario.'}
                </p>
              </header>

              {error && <Aviso mensaje={error} />}

              {modo === 'password' ? (
                <form onSubmit={enviar} className="space-y-4" noValidate>
                  <Campo
                    ref={refEmail}
                    etiqueta="Email"
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    placeholder="tu@correo.com"
                    icono={<Mail className="h-[18px] w-[18px]" />}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={errores.email?.[0]}
                    disabled={enviando}
                  />

                  <Campo
                    etiqueta="Contraseña"
                    type={verPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    icono={<Lock className="h-[18px] w-[18px]" />}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={errores.password?.[0]}
                    disabled={enviando}
                    sufijo={
                      <button
                        type="button"
                        onClick={() => setVerPassword((v) => !v)}
                        className="rounded p-2 text-tinta-tenue transition-colors hover:text-tinta"
                        aria-label={verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        tabIndex={-1}
                      >
                        {verPassword ? (
                          <EyeOff className="h-[18px] w-[18px]" />
                        ) : (
                          <Eye className="h-[18px] w-[18px]" />
                        )}
                      </button>
                    }
                  />

                  <button
                    type="submit"
                    className="btn-primario btn-toque mt-2 w-full"
                    disabled={enviando || !email.trim() || !password}
                  >
                    {enviando ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Entrando…
                      </>
                    ) : (
                      <>
                        Entrar <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>

                  {/* El enlace se ofrece SOLO si el sistema puede mandar el
                      mail (lo dice el servidor en la respuesta del local): un
                      botón que dispara un mail que nunca sale deja a la
                      persona esperando en el buzón, que es peor que no
                      ofrecerlo y tener que pedirle al dueño que le cambie la
                      contraseña desde Usuarios. */}
                  {tenant?.recuperacionDisponible && (
                    <p className="pt-1 text-center">
                      <Link
                        to={`/${tenant.slug}/recuperar`}
                        className="text-[13px] font-medium text-tinta-tenue underline decoration-borde-fuerte underline-offset-2 transition-colors hover:text-tinta"
                      >
                        Olvidé mi contraseña
                      </Link>
                    </p>
                  )}
                </form>
              ) : (
                <div>
                  {/* Dos pasos y no uno, igual que en la caja del mostrador, y
                      por el registro de autoría: buscar al dueño del PIN entre
                      todos los del local le atribuía la venta al que apareciera
                      primero cuando dos compartían número (con cuatro dígitos y
                      ocho empleados, casi 3% de chance). Acá el cajero toca su
                      nombre y el servidor verifica contra SU hash. */}
                  {!esCajaInstalada && empleados.length > 1 && (
                    <div className="mb-5">
                      <p className="mb-2 text-[13px] font-medium text-tinta-suave">
                        ¿Quién está en la caja?
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {empleados.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setElegido(u)
                              setPin('')
                              setError(null)
                            }}
                            className={
                              'rounded-xl border px-3 py-2 text-left transition ' +
                              (cajero?.id === u.id
                                ? 'border-verde bg-verde-tenue'
                                : 'border-borde bg-papel hover:border-borde-fuerte')
                            }
                          >
                            <span className="block text-[13.5px] font-semibold text-tinta">
                              {u.nombre}
                            </span>
                            <span className="block text-[12px] text-tinta-tenue">{u.rol}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!esCajaInstalada && consultaEmpleados.isError && (
                    <p className="mb-4 rounded-lg bg-ambar/10 px-3 py-2.5 text-[13px] text-ambar">
                      Este dispositivo dejó de estar registrado como caja. Entrá con mail y
                      contraseña, y volvé a registrarlo desde Configuración → Cajas.
                    </p>
                  )}

                  <PinPad
                    valor={pin}
                    onCambio={setPin}
                    onConfirmar={() => void enviar()}
                    deshabilitado={enviando || (!esCajaInstalada && cajero === null)}
                  />

                  {enviando && (
                    <p className="mt-5 flex items-center justify-center gap-2 text-[14px] text-tinta-tenue">
                      <Loader2 className="h-4 w-4 animate-spin" /> Verificando…
                    </p>
                  )}
                </div>
              )}

              {/* Cambio de modo. Se ofrece siempre: un encargado puede querer
                  entrar con contraseña desde la caja para tocar configuración. */}
              <div className="mt-7 border-t border-borde pt-5 text-center">
                {puedePin ? (
                  <button
                    type="button"
                    onClick={() => {
                      setModo(modo === 'pin' ? 'password' : 'pin')
                      setError(null)
                      setErrores({})
                      setPin('')
                    }}
                    className="text-[13.5px] font-semibold text-verde transition-colors hover:text-verde-oscuro"
                  >
                    {modo === 'pin' ? 'Entrar con mail y contraseña' : 'Entrar con PIN'}
                  </button>
                ) : (
                  // Sin registrar, el PIN no se ofrece: la nube lo rechaza y
                  // ofrecerlo sería mandar al cajero a un botón que no anda.
                  // Se dice cómo se habilita, que es lo único útil acá.
                  <p className="text-[12.5px] leading-relaxed text-tinta-tenue">
                    ¿Querés entrar con PIN desde este dispositivo? Se habilita una vez, desde
                    <strong> Configuración → Cajas</strong>, con «Usar este dispositivo».
                  </p>
                )}
              </div>

              {!config().capacidades.offline && (
                <p className="mt-6 text-center text-[12.5px] text-tinta-tenue">
                  ¿Otro local?{' '}
                  <button
                    type="button"
                    className="font-medium underline decoration-borde-fuerte underline-offset-2 hover:text-tinta"
                    onClick={() => {
                      setTenantManual(null)
                      setSlug('')
                      setError(null)
                      void navegar('/login', { replace: true })
                    }}
                  >
                    Cambiar
                  </button>
                </p>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}

// Paso previo cuando la URL no trae el local. Pasa cuando alguien entra a la
// raíz del sistema en lugar de a la dirección de su verdulería.
function PasoLocal({
  slug,
  setSlug,
  onEnviar,
  enviando,
  error,
  refSlug,
}: {
  slug: string
  setSlug: (v: string) => void
  onEnviar: (e: React.FormEvent) => void
  enviando: boolean
  error: string | null
  refSlug: React.RefObject<HTMLInputElement>
}) {
  return (
    <>
      <header className="mb-7">
        <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-tinta">
          ¿De qué local sos?
        </h1>
        <p className="mt-1.5 text-[14px] text-tinta-tenue">
          Es el nombre corto que aparece en la dirección de tu sistema.
        </p>
      </header>

      {error && <Aviso mensaje={error} />}

      <form onSubmit={onEnviar} className="space-y-4" noValidate>
        <Campo
          ref={refSlug}
          etiqueta="Local"
          placeholder="laesquina"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          icono={<Store className="h-[18px] w-[18px]" />}
          value={slug}
          onChange={(e) => setSlug(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())}
          ayuda="Por ejemplo, en app.fruver.ar/laesquina el local es «laesquina»."
          disabled={enviando}
        />

        <button
          type="submit"
          className="btn-primario btn-toque w-full"
          disabled={enviando || !slug.trim()}
        >
          {enviando ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Buscando…
            </>
          ) : (
            <>
              Continuar <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </>
  )
}

function Aviso({ mensaje }: { mensaje: string }) {
  return (
    <div
      role="alert"
      className="mb-5 flex items-start gap-2.5 rounded-lg border border-rojo/25 bg-rojo/[0.06] px-3.5 py-3"
    >
      <AlertCircle className="mt-0.5 h-[18px] w-[18px] shrink-0 text-rojo" />
      <p className="text-[13.5px] leading-snug text-tinta">{mensaje}</p>
    </div>
  )
}

// Identificador de la instalación de esta caja.
//
// Lo emite el ejecutable local al registrarse contra la nube y lo publica en
// config.json. En el navegador no existe, y eso es correcto: sin instalación
// registrada el PIN no autentica, porque un PIN de cuatro dígitos no puede
// ser una credencial válida desde internet.
function obtenerInstalacionId(): string {
  const delEjecutable = (window as { __FRUVER_INSTALACION__?: string }).__FRUVER_INSTALACION__

  if (delEjecutable !== undefined && delEjecutable.length > 0) return delEjecutable

  // En el navegador vale el id de ESTE navegador, y solo sirve si alguien lo
  // registró como caja desde Configuración → Cajas: la nube rechaza el PIN de
  // una instalación que no conoce.
  return cajaDeEsteNavegador() !== null ? instalacionDeEsteNavegador() : ''
}
